import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useT } from '../i18n/translations';
import { getArchivedItems, getArchiveStats, restoreArchivedItem } from '../archiveApi';
import { API_BASE } from '../config';
import './ArchivePage.css';

/* ── Entity type config ──────────────────────────────────────────────────── */
const ENTITY_TYPES = [
  { key: '',               labelKey: 'archive.all'            },
  { key: 'Decision',       labelKey: 'archive.typeDecision'   },
  { key: 'Meeting',        labelKey: 'archive.typeMeeting'    },
  { key: 'MeetingRecording', labelKey: 'archive.typeMeetingRecording' },
  { key: 'Task',           labelKey: 'archive.typeTask'       },
  { key: 'Committee',      labelKey: 'archive.typeCommittee'  },
  { key: 'OperationalPlan',labelKey: 'archive.typePlan'       },
];

const TYPE_COLORS = {
  Decision:        '#02c3a6',
  Meeting:         '#005f7a',
  MeetingRecording: '#c62828',
  Task:            '#f59e0b',
  Committee:       '#7c3aed',
  OperationalPlan: '#0ea5e9',
};

const TYPE_ICONS = {
  Decision:        <svg viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>,
  Meeting:         <svg viewBox="0 0 24 24"><path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z"/></svg>,
  MeetingRecording: <svg viewBox="0 0 24 24"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/></svg>,
  Task:            <svg viewBox="0 0 24 24"><path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>,
  Committee:       <svg viewBox="0 0 24 24"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>,
  OperationalPlan: <svg viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/></svg>,
};

/* ── Icon helpers ────────────────────────────────────────────────────────── */
const IconArchive    = () => <svg viewBox="0 0 24 24"><path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5l.81-1h12l.94 1H5.12z"/></svg>;
const IconRestore    = () => <svg viewBox="0 0 24 24"><path d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21a9 9 0 0 0 0-18zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/></svg>;
const IconSearch     = () => <svg viewBox="0 0 24 24"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>;
const IconEmpty      = () => <svg viewBox="0 0 24 24"><path d="M20 6h-2.18c.07-.44.18-.88.18-1.36C18 2.53 15.95.5 13.5.5c-1.36 0-2.53.56-3.4 1.47L9 3.08 7.9 1.97C7.03 1.06 5.86.5 4.5.5 2.05.5 0 2.53 0 4.64c0 .48.11.92.18 1.36H0v2h20V6zm-8.5-3.5c.83 0 1.5.67 1.5 1.5S12.33 5.5 11.5 5.5 10 4.83 10 4a1.5 1.5 0 0 1 1.5-1.5zm-7 0C5.33 2.5 6 3.17 6 4S5.33 5.5 4.5 5.5 3 4.83 3 4a1.5 1.5 0 0 1 1.5-1.5zM1 20c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V8H1v12zm8-7h2v2H9v-2zm0 4h2v2H9v-2zm4-4h2v2h-2v-2zm0 4h2v2h-2v-2zm-8-4h2v2H5v-2zm0 4h2v2H5v-2z"/></svg>;
const IconDownload   = () => <svg viewBox="0 0 24 24"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>;

/* ════════════════════════════════════════════════════════════════════════════
   ArchivePage
═══════════════════════════════════════════════════════════════════════════ */
export default function ArchivePage() {
  const t = useT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeTab,   setActiveTab]   = useState(() => searchParams.get('type') ?? '');
  const [search,      setSearch]      = useState('');
  const [debouncedQ,  setDebouncedQ]  = useState('');
  const [from,        setFrom]        = useState('');
  const [to,          setTo]          = useState('');
  const [page,        setPage]        = useState(1);
  const PAGE_SIZE = 18;

  const [data,        setData]        = useState(null);   // { total, items }
  const [stats,       setStats]       = useState([]);     // [{ entityType, count }]
  const [loading,     setLoading]     = useState(false);
  const [restoring,   setRestoring]   = useState(null);   // id being restored
  const [confirmId,   setConfirmId]   = useState(null);   // restore confirm

  /* Debounce search */
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(search), 400);
    return () => clearTimeout(id);
  }, [search]);

  /* Reset page on filter change */
  useEffect(() => { setPage(1); }, [activeTab, debouncedQ, from, to]);

  /* Load items */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getArchivedItems({
        entityType: activeTab || undefined,
        search:     debouncedQ || undefined,
        from:       from || undefined,
        to:         to   || undefined,
        page,
        pageSize:   PAGE_SIZE,
      });
      setData(res);
    } catch { /* ignore */ }
    setLoading(false);
  }, [activeTab, debouncedQ, from, to, page]);

  useEffect(() => { load(); }, [load]);

  /* Load stats once */
  useEffect(() => {
    getArchiveStats().then(setStats).catch(() => {});
  }, []);

  /* Restore */
  async function handleRestore(id) {
    setRestoring(id);
    try {
      await restoreArchivedItem(id);
      setConfirmId(null);
      load();
      getArchiveStats().then(setStats).catch(() => {});
    } catch { /* ignore */ }
    setRestoring(null);
  }

  /* Navigate to source */
  function handleOpen(item) {
    const routes = {
      Decision:        `/app/decisions/${item.entityId}`,
      Meeting:         `/app/meetings/${item.entityId}`,
      MeetingRecording: `/app/meetings/${item.entityId}`,
      Task:            `/app/tasks/${item.entityId}`,
      Committee:       `/app/committees/${item.entityId}`,
      OperationalPlan: `/app/operational-plans/${item.entityId}`,
    };
    const r = routes[item.entityType];
    if (r) navigate(r);
  }

  const total     = data?.total ?? 0;
  const items     = data?.items ?? [];
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  /* Stats lookup */
  function statCount(type) {
    return stats.find(s => s.entityType === type)?.count ?? 0;
  }
  const totalArchived = stats.reduce((s, x) => s + x.count, 0);

  return (
    <div className="archive-page">

      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="archive-page__header">
        <div className="archive-page__title-row">
          <span className="archive-page__icon"><IconArchive /></span>
          <div>
            <h1 className="archive-page__title">{t('archive.title')}</h1>
            <p className="archive-page__subtitle">{t('archive.subtitle')}</p>
          </div>
        </div>

        {/* Stats strip */}
        <div className="archive-page__stats">
          <div className="archive-stat archive-stat--total">
            <span className="archive-stat__num">{totalArchived}</span>
            <span className="archive-stat__lbl">{t('archive.totalArchived')}</span>
          </div>
          {ENTITY_TYPES.slice(1).map(et => (
            <div key={et.key} className="archive-stat" style={{ '--dot': TYPE_COLORS[et.key] }}>
              <span className="archive-stat__num">{statCount(et.key)}</span>
              <span className="archive-stat__lbl">{t(et.labelKey)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Tabs ───────────────────────────────────────────────── */}
      <div className="archive-page__tabs">
        {ENTITY_TYPES.map(et => (
          <button
            key={et.key}
            className={`archive-tab${activeTab === et.key ? ' archive-tab--active' : ''}`}
            style={activeTab === et.key && et.key ? { '--tab-color': TYPE_COLORS[et.key] } : {}}
            onClick={() => setActiveTab(et.key)}
          >
            {et.key && <span className="archive-tab__dot" style={{ background: TYPE_COLORS[et.key] }} />}
            {t(et.labelKey)}
            {et.key && statCount(et.key) > 0 && (
              <span className="archive-tab__badge">{statCount(et.key)}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Filters ─────────────────────────────────────────────── */}
      <div className="archive-page__filters">
        <div className="archive-search">
          <IconSearch />
          <input
            type="text"
            placeholder={t('archive.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="archive-date-range">
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
          <span>—</span>
          <input type="date" value={to}   onChange={e => setTo(e.target.value)} />
        </div>
        {(search || from || to) && (
          <button className="archive-clear-btn" onClick={() => { setSearch(''); setFrom(''); setTo(''); }}>
            {t('archive.clearFilters')}
          </button>
        )}
      </div>

      {/* ── Grid ────────────────────────────────────────────────── */}
      {loading ? (
        <div className="archive-page__loading">
          <div className="archive-spinner" />
          <p>{t('archive.loading')}</p>
        </div>
      ) : items.length === 0 ? (
        <div className="archive-page__empty">
          <span className="archive-page__empty-icon"><IconEmpty /></span>
          <p>{t('archive.empty')}</p>
        </div>
      ) : (
        <div className="archive-grid">
          {items.map(item => (
            <ArchiveCard
              key={item.id}
              item={item}
              t={t}
              confirmId={confirmId}
              restoring={restoring}
              onOpen={() => handleOpen(item)}
              onConfirmRestore={() => setConfirmId(item.id)}
              onCancelRestore={() => setConfirmId(null)}
              onRestore={() => handleRestore(item.id)}
            />
          ))}
        </div>
      )}

      {/* ── Pagination ──────────────────────────────────────────── */}
      {!loading && totalPages > 1 && (
        <div className="archive-page__pagination">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            ‹ {t('common.prev')}
          </button>
          <span>{t('archive.pageOf').replace('{page}', page).replace('{total}', totalPages)}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            {t('common.next')} ›
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Archive Card ─────────────────────────────────────────────────────────── */
function ArchiveCard({ item, t, confirmId, restoring, onOpen, onConfirmRestore, onCancelRestore, onRestore }) {
  const color = TYPE_COLORS[item.entityType] ?? '#888';
  const icon  = TYPE_ICONS[item.entityType];
  const isConfirming = confirmId === item.id;

  let meta = {};
  try { meta = JSON.parse(item.metaJson ?? '{}'); } catch {}

  return (
    <div className="archive-card" style={{ '--card-color': color }}>
      <div className="archive-card__top">
        <span className="archive-card__type-icon">{icon}</span>
        <div className="archive-card__type-badge" style={{ background: color }}>
          {t(`archive.type${item.entityType}`)}
        </div>
        {item.archiveSource === 'auto' && (
          <span className="archive-card__auto-badge">{t('archive.auto')}</span>
        )}
      </div>

      <h3 className="archive-card__title" title={item.title}>{item.title}</h3>

      {item.summary && (
        <p className="archive-card__summary">{item.summary}</p>
      )}

      {/* Meta fields */}
      <div className="archive-card__meta">
        {meta.status && (
          <span className="archive-card__meta-chip">{meta.status}</span>
        )}
        {meta.priority && (
          <span className="archive-card__meta-chip archive-card__meta-chip--priority">{meta.priority}</span>
        )}
      </div>

      <div className="archive-card__footer">
        <div className="archive-card__date">
          <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/></svg>
          {new Date(item.archivedAtUtc).toLocaleDateString()}
        </div>
        {item.archivedByName && (
          <span className="archive-card__by">{item.archivedByName}</span>
        )}
      </div>

      {/* Actions */}
      <div className="archive-card__actions">
        <button className="archive-card__btn archive-card__btn--open" onClick={onOpen}>
          {t('archive.viewSource')}
        </button>

        {item.fileUrl && (
          <a
            className="archive-card__btn archive-card__btn--download"
            href={`${API_BASE}${item.fileUrl}`}
            download
            target="_blank"
            rel="noreferrer"
          >
            <IconDownload /> {t('archive.download')}
          </a>
        )}

        {isConfirming ? (
          <div className="archive-card__confirm">
            <span>{t('archive.restoreConfirm')}</span>
            <button
              className="archive-card__btn archive-card__btn--restore"
              onClick={onRestore}
              disabled={restoring === item.id}
            >
              {restoring === item.id ? t('archive.restoring') : t('common.yes')}
            </button>
            <button className="archive-card__btn archive-card__btn--cancel" onClick={onCancelRestore}>
              {t('common.no')}
            </button>
          </div>
        ) : (
          <button className="archive-card__btn archive-card__btn--restore" onClick={onConfirmRestore}>
            <IconRestore />
            {t('archive.restore')}
          </button>
        )}
      </div>
    </div>
  );
}
